<template>
  <v-container class="pa-0 mx-5">
    <v-row
      no-gutters
      v-if="
        Array.isArray(attributeListFiltered) &&
        attributeListFiltered.length &&
        !loading
      "
    >
      <v-col xs="12" lg="8">
        <v-row>
          <p
            class="mt-1 mb-1"
            data-cy="created-tag"
            v-if="mode !== 'create' && showTimestampHeader === true"
          >
            Created on {{ formatTimestamp(entity.created) }} by
            {{
              (getUserById(entity.createdBy) || { email: "(Not Saved)" }).email
            }}
          </p>
        </v-row>
        <v-row class="mt-0">
          <v-divider class="mb-6" />
        </v-row>

        <v-row class="mt-0">
          <v-spacer />
          <v-btn
            class="mb-6"
            color="primary"
            data-cy="formSubmit"
            outlined
            small
            @click="goToEdit"
            ><v-icon small class="mr-2">fas fa-pencil-alt</v-icon> Edit</v-btn
          >
        </v-row>

        <v-alert
          dense
          type="info"
          outlined
          class="mt-3"
          v-if="infoAlertHTML && infoAlertHTML.length"
        >
          <span v-html="infoAlertHTML" />
        </v-alert>

        <form-entity
          :attributeList="attributeListFiltered"
          :entityName="entityName"
          mode="preview"
          :primaryKeyName="primaryKeyName"
          v-model="entityForEdit"
        ></form-entity>
      </v-col>
    </v-row>
    <generic-loader v-else></generic-loader>
  </v-container>
</template>

<script>
import { cloneDeep, pick } from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";

import editFormMixin from "./editFormMixin";
import FormEntity from "./components/Form";
import GenericLoader from "@/components/GenericLoader";

import { formatTimestamp } from "@/lib/componentUtil";

export default {
  components: {
    FormEntity,
    GenericLoader,
  },
  computed: {
    entityLabel() {
      const { entityName, getEntityTypeByName } = this;

      return (getEntityTypeByName({ name: entityName }) || { label: "Entity" })
        .label;
    },
    ...mapGetters({
      getEntityTypeByName: "entity/find",
      getUserById: "user/getById",
    }),
    ...mapState({
      organizationId: state => state.session.organizationId,
    }),
  },
  async created() {
    const { entityName, id } = this;

    this.loading = true;
    try {
      await Promise.all([
        this.getAttributeList({ reload: false }),
        this.getEntityTypeList({ reload: false }),
        this.getUserList({ reload: false }),
        this.entityGetList({ reload: false }),
      ]);
    } catch (e) {
      console.error(e);
      this.$notifyError({ message: "Unable to load org data." });
      return;
    }

    try {
      if (!id) throw new Error("Failed to load View.vue: no entityId");
      await this.loadEntity(id);
    } catch (e) {
      console.error(e);
      this.$notifyError({ message: `Unable to load ${entityName} data.` });
      return;
    }

    this.loading = false;
  },
  data() {
    return {
      loading: true,
      entityForEdit: {},
    };
  },
  methods: {
    async entityCreate(attributeMap) {
      return this.$store.dispatch(`${this.entityName}/create`, attributeMap);
    },
    async entityUpdate([id, attributeMap]) {
      return this.$store.dispatch(`${this.entityName}/update`, [
        id,
        attributeMap,
      ]);
    },
    getEntityById(id) {
      const { entityName } = this;

      const getterName = `${entityName}/getById`;

      return this.$store.getters[getterName](id);
    },
    goToEdit() {
      this.$router.push({ name: this.editRedirect });
    },
    async loadEntity(id) {
      const { mode, entityLabel } = this;
      // Make local copy for editing

      let entity;
      if (mode !== "create") {
        entity = cloneDeep(this.getEntityById(id));

        if (!entity) {
          throw new Error(`Could not load ${entityLabel}`);
        }
      } else {
        entity = {};
      }

      this.entity = entity;

      const editableAttributeNameList = this.attributeListFiltered.map(
        ({ name }) => name
      );

      // "locked" is re-added because it is needed for locations and departments to lock
      // This can be removed after completing https://jira.ochin.org/browse/TOOLS-6372
      editableAttributeNameList.push("locked");

      const entityForEdit = pick(entity, editableAttributeNameList);
      entityForEdit[this.primaryKeyName] = id;

      // Ref: https://vuejs.org/v2/guide/reactivity.html#For-Objects
      this.entityForEdit = Object.assign({}, this.entityForEdit, entityForEdit);
    },
    formatTimestamp,
    ...mapActions({
      getAttributeList: "attribute/list",
      getUserList: "user/list",
      getEntityTypeList: "entity/list",
    }),
  },
  mixins: [editFormMixin],
  props: {
    id: {
      type: Number,
      required: true,
    },
    infoAlertHTML: {
      type: String,
      required: false,
    },
    editRedirect: {
      type: String,
      required: true,
    },
    showTimestampHeader: {
      default: true,
      type: Boolean,
      required: false,
    },
  },
  watch: {
    id: async function (id) {
      this.loadEntity(id);
    },
  },
};
</script>
